import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Upload, ImageIcon, Sparkles, Trash2 } from "lucide-react";
import Logo from "../components/Logo";
import { api, useAuth } from "../context/AuthContext";

const BASE = process.env.REACT_APP_BACKEND_URL;

const SLOTS = [
  { key: "hero", label: "Foto principale (apertura sito)" },
  { key: "storia-ingresso", label: "Storia — arco d'ingresso" },
  { key: "storia-ieri", label: "Storia — foto \"Ieri\" (d'archivio, senza miglioramento AI)" },
  { key: "storia-oggi", label: "Storia — foto \"Oggi\"" },
  { key: "menu-taglieri-1", label: "Menù — Tagliere di salumi" },
  { key: "menu-taglieri-2", label: "Menù — Tagliere di formaggi" },
  { key: "piatto-1", label: "Piatti — Piatto 1" },
  { key: "piatto-2", label: "Piatti — Piatto 2" },
  { key: "piatto-3", label: "Piatti — Piatto 3" },
  { key: "piatto-4", label: "Piatti — Piatto 4" },
  { key: "gallery-0", label: "Galleria — La Sala delle Volte" },
  { key: "gallery-1", label: "Galleria — La Corte degli Ombrelli" },
  { key: "gallery-2", label: "Galleria — Il Dehors" },
  { key: "gallery-3", label: "Galleria — L'Angolo del Bar" },
  { key: "gallery-4", label: "Galleria — La Sala dello Specchio" },
  { key: "gallery-5", label: "Galleria — La Parete dei Vini" },
  { key: "gallery-6", label: "Galleria — Le Etichette" },
  { key: "gallery-7", label: "Galleria — La Saletta Riservata" },
  { key: "gallery-8", label: "Galleria — L'Arco d'Ingresso" },
  { key: "gallery-9", label: "Galleria — I Dettagli" },
];

const WINE_SLOTS = [1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({
  key: `carta-vini-${n}`,
  label: `Carta dei Vini — Pagina ${n}`,
}));

export default function AdminPhotos() {
  const { user } = useAuth();
  const [photos, setPhotos] = useState({});
  const [meta, setMeta] = useState({});
  const [uploading, setUploading] = useState(null);
  const [carniSlots, setCarniSlots] = useState([]);

  useEffect(() => {
    api.get("/carni")
      .then((r) => setCarniSlots((r.data || []).map((c) => ({ key: `lombate-${c.id}`, label: `Lombate — ${c.name || c.id}` }))))
      .catch(() => {});
  }, []);

  const slots = [...SLOTS.slice(0, 3), ...carniSlots, ...SLOTS.slice(3)];

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/photos/meta");
      const paths = {};
      const m = {};
      Object.entries(data || {}).forEach(([slot, rec]) => {
        paths[slot] = rec.path;
        m[slot] = { enhancing: !!rec.enhancing, enhanced: !!rec.enhanced };
      });
      setPhotos(paths);
      setMeta(m);
    } catch {
      toast.error("Errore nel caricamento delle foto");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const enhancingAny = Object.values(meta).some((x) => x.enhancing);
  useEffect(() => {
    if (!enhancingAny) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [enhancingAny, load]);

  const upload = async (slot, file) => {
    if (!file) return;
    setUploading(slot);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/photos/${slot}`, formData);
      await load();
      if (slot.startsWith("carta-vini")) {
        toast.success(
          <span data-testid="photo-uploaded-toast">
            Pagina caricata — la trascrivo nel menù, poi potrai eliminarla da qui
          </span>
        );
      } else {
        toast.success(
          <span data-testid="photo-uploaded-toast">
            Foto caricata — il miglioramento AI è in corso, la foto si aggiornerà da sola entro un minuto
          </span>
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(null);
    }
  };

  const del = async (slot) => {
    try {
      await api.delete(`/photos/${slot}`);
      await load();
      toast.success(<span data-testid="photo-deleted-toast">Foto rimossa</span>);
    } catch {
      toast.error("Rimozione non riuscita");
    }
  };

  const renderCard = (slot, i, isTemp = false) => {
    const m = meta[slot.key] || {};
    return (
      <motion.div
        key={slot.key}
        data-testid={`photo-slot-${slot.key}`}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: i * 0.03 }}
        className={`bg-[#171512] border rounded-2xl overflow-hidden transition-colors duration-500 ${
          m.enhancing ? "border-[#C9A265]/60" : "border-[rgba(255,255,255,0.08)]"
        }`}
      >
        <div className="relative h-44 bg-[#0D0C0A] flex items-center justify-center overflow-hidden">
          {photos[slot.key] ? (
            <img
              src={`${BASE}/api/files/${photos[slot.key]}`}
              alt={slot.label}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-[#3a342c]">
              <ImageIcon size={28} />
              <span className="text-[10px] uppercase tracking-[0.2em] font-cinzel">
                {isTemp ? "Nessuna pagina caricata" : "Foto predefinita del sito"}
              </span>
            </div>
          )}
          {m.enhancing && (
            <div
              data-testid={`photo-enhancing-${slot.key}`}
              className="absolute inset-x-0 bottom-0 bg-[#0D0C0A]/85 backdrop-blur-sm px-3 py-2 flex items-center gap-2"
            >
              <Sparkles size={13} className="text-[#C9A265] animate-pulse" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-cinzel text-[#C9A265]">
                Miglioramento AI in corso…
              </span>
            </div>
          )}
          {m.enhanced && !m.enhancing && (
            <div
              data-testid={`photo-enhanced-${slot.key}`}
              className="absolute top-2 right-2 bg-[#0D0C0A]/80 backdrop-blur-sm rounded-full px-2.5 py-1 flex items-center gap-1.5"
            >
              <Sparkles size={11} className="text-[#C9A265]" />
              <span className="text-[9px] uppercase tracking-[0.15em] font-cinzel text-[#C9A265]">AI</span>
            </div>
          )}
        </div>
        <div className="p-5">
          <p className="text-sm text-[#FAF7F2] font-serif-display text-lg leading-tight">{slot.label}</p>
          <input
            type="file"
            accept="image/*"
            className="sr-only"
            id={`file-${slot.key}`}
            data-testid={`photo-input-${slot.key}`}
            onChange={(e) => { upload(slot.key, e.target.files[0]); e.target.value = ""; }}
          />
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              data-testid={`photo-upload-btn-${slot.key}`}
              onClick={() => document.getElementById(`file-${slot.key}`)?.click()}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border text-xs uppercase tracking-[0.2em] font-cinzel rounded-full cursor-pointer transition-colors duration-300 ${
                uploading === slot.key
                  ? "border-[rgba(201,162,101,0.3)] text-[#7A7267] pointer-events-none"
                  : "border-[#C9A265] text-[#C9A265] hover:bg-[#C9A265] hover:text-[#0D0C0A]"
              }`}
            >
              <Upload size={14} /> {uploading === slot.key ? "Caricamento..." : photos[slot.key] ? "Sostituisci" : "Carica foto"}
            </button>
            {photos[slot.key] && (
              <button
                type="button"
                data-testid={`photo-delete-btn-${slot.key}`}
                onClick={() => del(slot.key)}
                title="Rimuovi foto"
                className="w-[46px] rounded-full border border-red-800/50 text-red-300 flex items-center justify-center hover:bg-red-950/50 transition-colors"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div data-testid="admin-photos-page" className="min-h-screen bg-[#0D0C0A]">
      <header className="border-b border-[rgba(201,162,101,0.18)] bg-[#171512]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-9 w-9" />
            <div>
              <p className="font-cinzel text-sm tracking-[0.25em] text-[#FAF7F2]">GESTIONE FOTO</p>
              <p className="text-[11px] text-[#7A7267]">{user?.email}</p>
            </div>
          </div>
          <a
            href="/admin"
            data-testid="photos-back-to-reservations"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#B0A89C] hover:text-[#C9A265] font-cinzel transition-colors"
          >
            <ArrowLeft size={14} /> Prenotazioni
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
        <p className="text-sm text-[#7A7267] mb-8 max-w-xl leading-relaxed">
          Carica una foto per sostituire quella corrispondente sul sito. Vanno bene tutti i formati,
          anche le foto HEIC dell'iPhone: le ottimizziamo noi in automatico.{" "}
          <span className="text-[#C9A265]">Ogni foto caricata viene migliorata automaticamente con l'AI</span>{" "}
          (luce, colori e nitidezza): vedrai la versione migliorata comparire qui entro un minuto.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {slots.map((slot, i) => renderCard(slot, i))}
        </div>

        <div className="mt-16 border-t border-[rgba(201,162,101,0.15)] pt-10" data-testid="wine-list-photos-section">
          <h2 className="font-serif-display text-2xl text-[#FAF7F2]">Carta dei Vini — pagine da trascrivere</h2>
          <p className="mt-2 text-sm text-[#7A7267] max-w-xl leading-relaxed">
            Carica qui le foto delle pagine della carta dei vini:{" "}
            <span className="text-[#C9A265]">non compaiono sul sito</span> e non vengono ritoccate dall'AI,
            così il testo resta nitido e leggibile. Le trascrivo nel menù (italiano e inglese) e poi puoi
            eliminarle con il cestino.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WINE_SLOTS.map((slot, i) => renderCard(slot, i, true))}
          </div>
        </div>
      </main>
    </div>
  );
}
